/**
 * Next Visit Validation Utilities
 *
 * Pure functions that validate next visit data without side effects.
 * These functions return validation results that tell you what action to take.
 */

/**
 * Validation result types
 */
export const VALIDATION_RESULT = {
  ALLOW_LEAVE: 'ALLOW_LEAVE', // User can leave without any dialogs
  SHOW_NO_NEXT_VISIT_DIALOG: 'SHOW_NO_NEXT_VISIT_DIALOG', // Show "why no next visit" dialog
  SHOW_HOLD_SLOT_DIALOG: 'SHOW_HOLD_SLOT_DIALOG', // Show "why no slot held" dialog
}

/**
 * Validates the next visit data when user tries to leave the session
 *
 * @param {Object} params - Validation parameters
 * @param {boolean} params.markAsCompleted - Whether session is being marked as completed
 * @param {number|string} params.nextVisitInputValue - Number of days/weeks/months for next visit
 * @param {boolean} params.showNextVisitSlotSelector - Whether slot selector feature is enabled
 * @param {string} params.nextVisitReferenceBookedEventId - ID of already booked next visit
 * @param {string} params.nextVisitHoldSlotOmissionReason - Reason for not holding a slot
 *
 * @returns {Object} Validation result with action to take
 * @returns {string} result.action - One of VALIDATION_RESULT values
 * @returns {string} result.reason - Human-readable reason for the action
 *
 * @example
 * const result = validateNextVisitBeforeLeaving({
 *   markAsCompleted: true,
 *   nextVisitInputValue: null,
 *   showNextVisitSlotSelector: true,
 *   nextVisitReferenceBookedEventId: null,
 *   nextVisitHoldSlotOmissionReason: null
 * })
 *
 * if (result.action === VALIDATION_RESULT.SHOW_NO_NEXT_VISIT_DIALOG) {
 *   // Show dialog asking why no next visit was set
 * }
 */
export function validateNextVisitBeforeLeaving({
  markAsCompleted,
  nextVisitInputValue,
  showNextVisitSlotSelector,
  nextVisitReferenceBookedEventId,
  nextVisitHoldSlotOmissionReason,
}) {
  // If not marking as completed, allow leaving without validation
  if (!markAsCompleted) {
    return {
      action: VALIDATION_RESULT.ALLOW_LEAVE,
      reason: 'Session not being marked as completed',
    }
  }

  // CASE 0: If a follow-up event is already booked, allow leaving (no validation needed)
  if (nextVisitReferenceBookedEventId) {
    return {
      action: VALIDATION_RESULT.ALLOW_LEAVE,
      reason: 'Next visit event is already booked',
    }
  }

  // CASE 1: No next visit date set at all
  if (!nextVisitInputValue) {
    return {
      action: VALIDATION_RESULT.SHOW_NO_NEXT_VISIT_DIALOG,
      reason: 'Next visit date is not set',
    }
  }

  // CASE 2: Next visit date is set, but slot holding is required and not done
  const isSlotHoldingRequired =
    showNextVisitSlotSelector && !nextVisitReferenceBookedEventId && !nextVisitHoldSlotOmissionReason

  if (isSlotHoldingRequired) {
    return {
      action: VALIDATION_RESULT.SHOW_HOLD_SLOT_DIALOG,
      reason: 'Next visit date set but slot not held and no omission reason provided',
    }
  }

  // CASE 3: All validations passed
  return {
    action: VALIDATION_RESULT.ALLOW_LEAVE,
    reason: 'All next visit requirements met',
  }
}

/**
 * Checks if next visit requirements are met for saving
 *
 * @param {Object} params - Same as validateNextVisitBeforeLeaving
 * @returns {boolean} True if can save without prompting user
 */
export function canSaveWithoutPrompt(params) {
  const result = validateNextVisitBeforeLeaving(params)
  return result.action === VALIDATION_RESULT.ALLOW_LEAVE
}

/**
 * Gets a user-friendly message for a validation result
 *
 * @param {string} action - One of VALIDATION_RESULT values
 * @returns {string} User-friendly message
 */
export function getValidationMessage(action) {
  switch (action) {
    case VALIDATION_RESULT.SHOW_NO_NEXT_VISIT_DIALOG:
      return 'Please provide a reason for not setting a next visit date'
    case VALIDATION_RESULT.SHOW_HOLD_SLOT_DIALOG:
      return 'Please hold a slot for the next visit or provide a reason for skipping'
    case VALIDATION_RESULT.ALLOW_LEAVE:
      return 'You can proceed'
    default:
      return 'Unknown validation status'
  }
}
